import React, { useState, useRef } from 'react';
import { Field, reduxForm, formValues, reset} from 'redux-form';
import { connect } from 'react-redux'
import Loader from 'Loader.js';
import { sendParagonexTransferData } from 'integrationActions.js';
import _get from 'lodash/get';
import Box from 'react-styled-box';
import { translation } from 'helpers/utilsHelper.js';
import ReactNotification from "react-notifications-component";
import S from 'StyledParagonexTransfer';

// Validation
import validation from 'sb-validate-js/validate.js';
import validators from 'validators.enum.js';

const min = 20000;
const max = 111000;

const validate = values => {
    const errors = validation(values, validators.PARAGONEX_TRANSFER);
    return errors;
};

const minValue = min => value =>
    value && value < min && [`${translation('paragonex_valueTooSmall')} ${min}`] || undefined;
const maxValue = max => value =>
    value && value > max && [`${translation('paragonex_valueTooBig')} ${max}`]  || undefined;

const setMinValue  = minValue(min);
const setMaxValue  = maxValue(max);

const addNotification = (type, title, message, ref, container = 'top-center', insert = 'top') => {
    removeNotification(ref);
    const messageText = translation(message);
    const titleText = translation(title)


    ref.current.addNotification({
        title: titleText,
        message: messageText,
        type: type,
        insert: insert,
        container: container,
        animationIn: ["animated", "fadeIn"],
        animationOut: ["animated", "fadeOut"],
        dismissable: { click: true },
    });
}

const removeNotification = (ref) => {
    const { notifications } = ref.current.state;

    if (Array.isArray(notifications) && notifications.length) {
        ref.current.removeNotification(notifications[0].id);
    }
}

let TransferMethodChooser = ({ transferMethod, change, confirmStep }) => {
    return (
        <S.TransferMethodChooser>
            <S.TransferMethod isActive={(transferMethod == 'deposit')}
                onClick={!confirmStep && change.bind(null, 'transferMethod', 'deposit')}>
                {translation('paragonex_depositTransfer')}
            </S.TransferMethod>
            <S.TransferMethod isActive={(transferMethod == 'payout')}
                onClick={!confirmStep && change.bind(null, 'transferMethod', 'payout')}>
                {translation('paragonex_payoutTransfer')}
            </S.TransferMethod>
        </S.TransferMethodChooser>
    )
};
TransferMethodChooser = formValues('transferMethod')(TransferMethodChooser);

let TransferMethodField = ({transferMethod}) => {
        
    return (transferMethod == 'deposit') ?
    (
        <S.TransferMethodField>
            <Field
                name="depositAmount"
                type="number"
                component={renderField}
                label={translation('paragonex_amount')}
                validate={[setMinValue, setMaxValue]}
            />
        </S.TransferMethodField>
    )
    :
    (
        <S.TransferMethodField>
            <Field
                name="payoutAmount"
                type="number"
                component={renderField}
                label={translation('paragonex_amount')}
                validate={[setMinValue, setMaxValue]}
            />
        </S.TransferMethodField>
    )   
};
TransferMethodField = formValues('transferMethod')(TransferMethodField);

const renderField = ({
    input,
    label,
    type,
    disabled,
    meta: { touched, error, warning}
}) => {

    return (
        <S.Row>
            <S.InputWrapper>
                <S.Label>{label}</S.Label>
                <S.Input {...input} type={type} disabled={disabled} required="required"/>
                {touched && ((error && <S.Error>{error}</S.Error>) || (warning && <S.Error>{warning}</S.Error>))}

            </S.InputWrapper>
        </S.Row>
    )
};


let ParagonexTransfer = ({ change, handleSubmit, submitting, transferSession, pristine, invalid, resetForm }) => {
    const [currentTransferSession, setCurrentTransferSession] = useState(transferSession);
    const [confirmStep, setConfirmStep] = useState(false);
    const [transferAmount, setTransferAmount] = useState(false);
    const notificationDOMRef = useRef(null);

    const sendData = async (formData, dispatch) => {
        
        const {transferMethod} = formData;

        if (confirmStep) {
            const postData = {
                amount: transferMethod == 'deposit' ? formData.depositAmount : formData.payoutAmount,
                transferType: transferMethod == 'deposit' ? 'payout' : 'deposit',
                transferSession: currentTransferSession
            }
        
            try {
                const {transferSuccess, transferSession, currentBalanceValue} = await dispatch(sendParagonexTransferData(postData));
                if (transferSuccess) {
                    setCurrentTransferSession(transferSession);
                    app.partial.TopMenuCustomerInfo.changeBalanceValue(currentBalanceValue.toFixed(2));
                    addNotification('success', transferMethod == 'deposit' ? 'paragonex_successDeposit' : 'paragonex_successPayout', 'paragonex_moneyTransferred', notificationDOMRef);
                } else {
                    addNotification('danger', `paragonex`, 'paragonex_generalError', notificationDOMRef);
                }

                dispatch(reset('ParagonexTransfer'));
                setConfirmStep(false);
            } catch (error) {
                setConfirmStep(false);
                dispatch(reset('ParagonexTransfer'));
                addNotification('danger', `paragonex`, error.message, notificationDOMRef);
            } 
        } else {
            removeNotification(notificationDOMRef);
            setTransferAmount(transferMethod == 'deposit' ? formData.depositAmount : formData.payoutAmount);
            setConfirmStep(true);
        }

    }

    return (
        <S.ParagonexTransfer onSubmit={handleSubmit(sendData)} autocomplete="off" noValidate>
            {submitting && (<S.Cover><Loader color="#F05A22" /></S.Cover>)}

            <S.Body>
                <Box flexDirection="column">

                    <TransferMethodChooser change={change} confirmStep={confirmStep}/>
                    <ReactNotification ref={notificationDOMRef} />

                    <S.Limits>
                        <S.LimitsRow>
                            {`${translation('paragonex_minTransfer')} ${min} ${process.env.MAIN_CURRENCY_CODE}`} 
                        </S.LimitsRow>
                        <S.LimitsRow>
                            {`${translation('paragonex_maxTransfer')} ${max} ${process.env.MAIN_CURRENCY_CODE}`} 
                        </S.LimitsRow>
                    </S.Limits>

                    {(!confirmStep) ? (
                            <>
                                <TransferMethodField />
                                <S.SubmitBtn type="submit" disabled={invalid || submitting || pristine}> 
                                    {translation('paragonex_transfer_next')}
                                </S.SubmitBtn>
                            </>
                        )
                        :
                        (
                            <>
                                <S.ConfrimValue>{`${translation('paragonex_amount_confirm').replace('{transferAmount}', transferAmount)} ${process.env.MAIN_CURRENCY_CODE}`}</S.ConfrimValue>
                                <Box flexDirection="row" justifyContent="space-around">
                                    <S.SubmitBtn type="submit" disabled={submitting}> 
                                        {translation('paragonex_transfer_confirm')}
                                    </S.SubmitBtn>
                                    <S.CancelBtn  disabled={submitting} onClick={() => setConfirmStep(false)}> 
                                        {translation('paragonex_transfer_back')}
                                    </S.CancelBtn>
                                </Box>
                            </>
                        )
                    }
                </Box>
            </S.Body>
        </S.ParagonexTransfer>
    )
}

const mapStateToProps = (state, props) => {

    return {
        initialValues: {
            transferMethod: 'deposit'
        }
    }
}

ParagonexTransfer = reduxForm({
    form: 'ParagonexTransfer',
    validate,
})(ParagonexTransfer)

export default connect(mapStateToProps)(ParagonexTransfer);


